import type { ChatItem, ToolUseItem } from '../../../contracts/harness.types';
import { isEvidenceSubagentToolName } from '../components/EvidenceSessionInlineBlock.utils';
import {
  resolveToolPresentation,
  type ToolCardKind,
} from '../timeline/presentation/tool-registry';

export interface TurnLifecycle {
  lastItem: ChatItem | undefined;
  /** Last `user_message` id, reverse scan. */
  latestUserMessageId: string | null;
  /** Settled `assistant_text` | terminal `tool_use`. */
  latestTurnHasReply: boolean;
  /** Tail `assistant_text` already painting words. */
  latestReplyHasText: boolean;
  /** Any `assistant_text` with words since the latest `user_message` — the
   *  tail alone goes false again on the next `tool_use`. */
  latestTurnHasReplyText: boolean;
  /** `turn_cancelled_marker` | `turn_failed` | `prompt_context_failed`. */
  latestTurnIsTerminal: boolean;
}

const getLatestTurnHasReply = (item: ChatItem | undefined): boolean =>
  item?.kind === 'assistant_text'
    ? !item.streaming || (item.text?.trim().length ?? 0) > 0
    : item?.kind === 'tool_use'
      ? item.status === 'complete' ||
        item.status === 'error' ||
        item.status === 'cancelled'
      : false;

const getLatestReplyHasText = (item: ChatItem | undefined): boolean =>
  item?.kind === 'assistant_text' && (item.text?.trim().length ?? 0) > 0;

/** One reverse scan to the latest `user_message`, reading the turn on the way. */
const scanLatestTurn = (
  items: ChatItem[]
): Pick<TurnLifecycle, 'latestUserMessageId' | 'latestTurnHasReplyText'> => {
  let latestTurnHasReplyText = false;
  for (let i = items.length - 1; i >= 0; i -= 1) {
    const item = items[i];
    if (item?.kind === 'user_message') {
      return { latestUserMessageId: item.id, latestTurnHasReplyText };
    }
    if (getLatestReplyHasText(item)) latestTurnHasReplyText = true;
  }
  return { latestUserMessageId: null, latestTurnHasReplyText };
};

// Cards that frame a long nested job rather than deliver a result. Their
// answer words arrive through the nested answer-text source instead.
const PROGRESS_CARD_KINDS: ReadonlySet<ToolCardKind> = new Set([
  'evidence-subagent',
  'verify-subagent',
  'child-run',
]);

const isResultToolUse = (item: ToolUseItem): boolean => {
  if (item.status === 'streaming_input' || item.status === 'queued') return false;
  const { card, interaction } = resolveToolPresentation(item);
  if (interaction !== undefined) return true;
  return card !== undefined && !PROGRESS_CARD_KINDS.has(card);
};

/**
 * Whether an item shows the user a result: answer words, an artifact or
 * result card, a plan, a question back to them, or a failure. Thinking,
 * search and plain Working rows are progress.
 */
const isTurnResultItem = (item: ChatItem): boolean => {
  switch (item.kind) {
    case 'assistant_text':
      return (item.text?.trim().length ?? 0) > 0;
    case 'tool_use':
      return isResultToolUse(item);
    case 'turn_failed':
    case 'prompt_context_failed':
      return true;
    default:
      return false;
  }
};

/** Whether the latest turn has shown the user a result yet. */
export const latestTurnHasResult = (items: readonly ChatItem[]): boolean => {
  for (let index = items.length - 1; index >= 0; index -= 1) {
    const item = items[index];
    if (item?.kind === 'user_message') return false;
    if (item && isTurnResultItem(item)) return true;
  }
  return false;
};

export interface ProjectTurnGroup {
  key: string;
  items: ChatItem[];
}

/** Splits the timeline into user-initiated turns: by ``turnId`` first, else
 *  at each visible user message. Items before the first turn are leading. */
export const groupProjectTurns = (
  items: readonly ChatItem[],
  isVisibleUserMessage: (item: ChatItem, isFirstUserMessage: boolean) => boolean
): { leadingItems: ChatItem[]; turnGroups: ProjectTurnGroup[] } => {
  const firstUserMessageId = items.find((entry) => entry.kind === 'user_message')?.id;
  const leadingItems: ChatItem[] = [];
  const turnGroups: ProjectTurnGroup[] = [];
  let currentTurnGroup: ProjectTurnGroup | null = null;
  let currentTurnId: string | undefined;

  for (const item of items) {
    const turnId = item.kind === 'prompt_context_failed' ? undefined : item.turnId;
    const sameTurn = turnId !== undefined && turnId === currentTurnId;
    const switchesTurn =
      currentTurnGroup !== null &&
      currentTurnId !== undefined &&
      turnId !== undefined &&
      turnId !== currentTurnId;
    const opensByUserMessage =
      !sameTurn &&
      item.kind === 'user_message' &&
      isVisibleUserMessage(item, item.id === firstUserMessageId);

    if (switchesTurn || opensByUserMessage) {
      currentTurnId = turnId;
      currentTurnGroup = { key: turnId ?? item.id, items: [item] };
      turnGroups.push(currentTurnGroup);
      continue;
    }

    if (currentTurnGroup) {
      currentTurnGroup.items.push(item);
      if (currentTurnId === undefined && turnId !== undefined) {
        currentTurnId = turnId;
        currentTurnGroup.key = turnId;
      }
    } else {
      leadingItems.push(item);
    }
  }

  return { leadingItems, turnGroups };
};

export const findTrailingEvidenceChildToolCallId = (
  items: readonly ChatItem[]
): string | null => {
  for (let index = items.length - 1; index >= 0; index -= 1) {
    const item = items[index];
    if (
      item?.kind === 'thinking' ||
      (item?.kind === 'assistant_text' && !item.text?.trim())
    ) {
      continue;
    }
    return item?.kind === 'tool_use' &&
      item.status === 'complete' &&
      item.childAgentRunId &&
      isEvidenceSubagentToolName(item.name)
      ? item.id
      : null;
  }
  return null;
};

/** Derives every "what state is the latest turn in" predicate in one pass. */
export const deriveTurnLifecycle = (items: ChatItem[]): TurnLifecycle => {
  const lastItem = items.at(-1);
  return {
    lastItem,
    ...scanLatestTurn(items),
    latestTurnHasReply: getLatestTurnHasReply(lastItem),
    latestReplyHasText: getLatestReplyHasText(lastItem),
    latestTurnIsTerminal:
      lastItem?.kind === 'turn_cancelled_marker' ||
      lastItem?.kind === 'turn_failed' ||
      lastItem?.kind === 'prompt_context_failed',
  };
};

export interface TurnActivityFlags {
  turnInFlight: boolean;
  isSubmitting: boolean;
  hasQueuedTurns: boolean;
  runFailed: boolean;
  runClosed: boolean;
  hasPendingApproval: boolean;
  inFlightDesktopCall: boolean;
  isStartingSeededTurn: boolean;
  isPreviewingUserMessage: boolean;
  /** A live Working section already owns the progress chrome. */
  hasLiveWorkingSection: boolean;
}

export interface ChatBusyInputs {
  turnInFlight: boolean;
  isSubmitting: boolean;
  hasQueuedTurns?: boolean;
}

/** A queued turn counts as busy, so a turn boundary never reads as idle. */
export const deriveChatBusy = ({
  turnInFlight,
  isSubmitting,
  hasQueuedTurns = false,
}: ChatBusyInputs): boolean => turnInFlight || isSubmitting || hasQueuedTurns;

export const deriveShowThinking = (
  lifecycle: TurnLifecycle,
  flags: TurnActivityFlags
): boolean =>
  (flags.isStartingSeededTurn ||
    flags.isPreviewingUserMessage ||
    (deriveChatBusy(flags) && !lifecycle.latestReplyHasText)) &&
  !flags.runFailed &&
  !flags.runClosed &&
  !flags.inFlightDesktopCall &&
  !flags.hasPendingApproval &&
  !flags.hasLiveWorkingSection;

/**
 * From the send until the turn's first result. Unlike the thinking indicator,
 * it does not return between paragraphs.
 */
export const deriveAwaitingReply = (
  lifecycle: TurnLifecycle,
  flags: TurnActivityFlags
): boolean =>
  (flags.isStartingSeededTurn ||
    flags.isPreviewingUserMessage ||
    deriveChatBusy(flags)) &&
  !lifecycle.latestTurnHasReplyText &&
  !flags.runFailed &&
  !flags.runClosed &&
  !flags.inFlightDesktopCall &&
  !flags.hasPendingApproval;

export const deriveLatestTurnNeedsRoomLive = (
  lifecycle: TurnLifecycle,
  flags: TurnActivityFlags
): boolean =>
  !flags.runFailed &&
  !flags.runClosed &&
  (flags.isPreviewingUserMessage ||
    flags.turnInFlight ||
    flags.isSubmitting ||
    (!lifecycle.latestTurnHasReply && !lifecycle.latestTurnIsTerminal));
